"use client";

import { useState, useRef, useEffect } from "react";
import { GraduationCap, ExternalLink } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { phCapture } from "@/utils/analytics";
import {
  type EmphasisLevel,
  readTutorialHintState,
  updateStateOnOpen,
  updateStateOnClose,
  computeEmphasisLevel,
  createToolKey,
} from "@/utils/tutorial-emphasis";

import { parseYouTubeUrl, buildEmbedUrl } from "@/utils/youtube";

const UPSIDE_YOUTUBE_CHANNEL = "https://www.youtube.com/@UpsideTools";

type TutorialIconLinkProps = {
  videoUrl: string;
  toolLabel: string;
  toolId?: string;
  channelUrl?: string;
  className?: string;
  tooltipSide?: "top" | "right" | "bottom" | "left";
};

function getEmphasisClass(level: EmphasisLevel): string {
  switch (level) {
    case "pulse":
      return "animate-tutorial-pulse";
    case "glow":
      return "ring-1 ring-primary/30 shadow-glow-primary-subtle";
    case "none":
    default:
      return "";
  }
}

export function TutorialIconLink({
  videoUrl,
  toolLabel,
  toolId,
  channelUrl = UPSIDE_YOUTUBE_CHANNEL,
  className,
  tooltipSide = "bottom",
}: TutorialIconLinkProps) {
  const [open, setOpen] = useState(false);
  const { youtubeId, startSeconds } = parseYouTubeUrl(videoUrl);

  const [emphasis, setEmphasis] = useState<EmphasisLevel>("none");
  const [hasMounted, setHasMounted] = useState(false);

  const openStartedAtRef = useRef<number | null>(null);

  const stableToolKey = createToolKey(toolId, toolLabel);

  useEffect(() => {
    setHasMounted(true);
    const state = readTutorialHintState(stableToolKey);
    const level = computeEmphasisLevel(state);
    setEmphasis(level);
  }, [stableToolKey]);

  const handleClick = () => {
    phCapture("tutorial_learn_clicked", {
      tool_id: stableToolKey,
      tool_label: toolLabel,
      video_url: videoUrl,
    });

    updateStateOnOpen(stableToolKey);

    openStartedAtRef.current = Date.now();

    setOpen(true);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && open) {
      const durationMs = openStartedAtRef.current
        ? Date.now() - openStartedAtRef.current
        : 0;

      const { action } = updateStateOnClose(stableToolKey, durationMs);

      phCapture("tutorial_closed", {
        tool_id: stableToolKey,
        tool_label: toolLabel,
        duration_ms: durationMs,
        dismissed: action === "dismissed",
        watched: action === "watched",
      });

      const state = readTutorialHintState(stableToolKey);
      const newLevel = computeEmphasisLevel(state);
      setEmphasis(newLevel);

      openStartedAtRef.current = null;
    }
    setOpen(newOpen);
  };

  const emphasisClass = hasMounted ? getEmphasisClass(emphasis) : "";

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleClick}
              data-tour="learn-button"
              className={cn(
                "group relative inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 before:absolute before:-inset-y-2 before:-inset-x-1 before:content-['']",
                "text-primary/90 hover:text-primary",
                "bg-transparent hover:bg-primary/10",
                "border border-primary/40 hover:border-primary/60",
                "transition-all duration-200 ease-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                "motion-reduce:animate-none",
                emphasisClass,
                className
              )}
              aria-label={`Watch ${toolLabel} tutorial`}
            >
              <GraduationCap className="size-3.5" />
              <span className="hidden text-xs font-medium sm:inline">Learn</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide} sideOffset={8}>
            <span className="text-sm">Watch tutorial</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-3xl p-0" contentClassName="p-0 gap-0" aria-describedby={undefined}>
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-base font-medium text-foreground/90">
              {toolLabel} Tutorial
            </DialogTitle>
          </DialogHeader>

          <div className="p-4 pt-3">
            {open && youtubeId && (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-black/50 border border-white/[0.06]">
                <iframe
                  src={buildEmbedUrl(youtubeId, startSeconds)}
                  title={`${toolLabel} Tutorial`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            )}

            {!youtubeId && (
              <div className="aspect-video rounded-lg bg-black/20 border border-white/[0.06] flex items-center justify-center">
                <span className="text-muted-foreground text-sm">Video unavailable</span>
              </div>
            )}

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm",
                  "text-muted-foreground hover:text-foreground",
                  "transition-colors duration-150"
                )}
              >
                <ExternalLink className="size-3.5" />
                Watch on YouTube
              </a>

              <a
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "text-xs text-muted-foreground/60 hover:text-muted-foreground",
                  "transition-colors duration-150"
                )}
              >
                More tutorials →
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
